/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms/views/View'
import { Pressable } from '@components/atoms/button/Pressable'
import { mono, boFont, boMotion } from './backoffice.tokens'

export interface BoSegmentItem {
	id: string
	label: string
	/** 오른쪽 작은 수(개수) */
	count?: number
}

interface PropsType {
	items: BoSegmentItem[]
	value: string
	onChange: (id: string) => void
	/** 'tabs' = 밑줄 탭(폰 자세히 섹션) · 'chips' = 칩 줄(상태 필터·태블릿 앵커) */
	variant?: 'tabs' | 'chips'
	className?: string
}

/**
 * 가로 스크롤 한 줄 — 이 줄만 가로로 움직인다 (RESPONSIVE_PLAN §8: 가로 스크롤은 칩 줄·탭 줄만).
 * 높이 44(터치 목표). 활성 = ink + 밑줄 1px(tabs) / solid 칩(chips).
 */
export const BoSegments = (props: PropsType) => {
	const variant = props.variant ?? 'tabs'
	const ref = React.useRef<HTMLDivElement | null>(null)

	// 활성 항목이 보이도록 스크롤
	React.useEffect(() => {
		const root = ref.current
		if (!root) return
		const el = root.querySelector<HTMLElement>(`[data-seg="${props.value}"]`)
		if (el && typeof el.scrollIntoView === 'function') {
			el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
		}
	}, [props.value])

	return (
		<View
			css={style}
			className={`boSegments ${variant} ${props.className ?? ''}`}
			ref={ref}
			role="tablist"
		>
			{props.items.map(item => {
				const active = item.id === props.value
				return (
					<Pressable
						key={item.id}
						className={`boSeg${active ? ' active' : ''}`}
						data-seg={item.id}
						role="tab"
						aria-selected={active}
						onClick={() => props.onChange(item.id)}
					>
						<span className="boSegLabel">{item.label}</span>
						{item.count !== undefined ? (
							<span className="boSegCount">{item.count}</span>
						) : null}
					</Pressable>
				)
			})}
		</View>
	)
}

const style = css({
	display: 'flex',
	alignItems: 'stretch',
	gap: 4,
	height: 44,
	minHeight: 44,
	overflowX: 'auto',
	overflowY: 'hidden',
	scrollbarWidth: 'none',
	'&::-webkit-scrollbar': { display: 'none' },
	fontFamily: boFont.ui,
	WebkitOverflowScrolling: 'touch',
	touchAction: 'pan-x',

	'.boSeg': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		flexShrink: 0,
		padding: '0 12px',
		fontFamily: boFont.ui,
		fontSize: 14,
		fontWeight: 400,
		color: mono.dim,
		whiteSpace: 'nowrap',
		transition: boMotion,
		'.boSegCount': {
			fontSize: 11,
			color: mono.faint,
			fontVariantNumeric: 'tabular-nums',
		},
	},

	'&.tabs': {
		borderBottom: `1px solid ${mono.line}`,
		'.boSeg': {
			borderBottom: '1px solid transparent',
			marginBottom: -1,
		},
		'.boSeg.active': {
			color: mono.ink,
			fontWeight: 500,
			borderBottomColor: mono.ink,
		},
	},

	'&.chips': {
		alignItems: 'center',
		gap: 8,
		'.boSeg': {
			height: 32,
			borderRadius: 2,
			border: `1px solid ${mono.lineSoft}`,
			fontSize: 13,
		},
		'.boSeg.active': {
			background: mono.ink,
			borderColor: mono.ink,
			color: '#FFFFFF',
			'.boSegCount': { color: 'rgba(255,255,255,.7)' },
		},
	},
})
